import {
  ProviderLocationMapping,
  ProviderLocationMappingSearch,
  SearchProviderLocationMappingResponseProviderLocationMappingConnectionArgs,
} from '@/models/gen/graphql';
import {
  ConnectionDetails,
  HandleErrorInput,
  convertConnectionToDetails,
  handleError,
  mergeConnectionDetails as merge,
} from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { SearchProviderLocationMappingsDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Global Service Variables
const mergeKey = 'searchProviderLocationMapping.providerLocationMappingConnection';
const onError = (res: HandleErrorInput): void => handleError(res, { notification: { title: 'Search Client Mappings' } });

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof ProviderLocationMappingSearch]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<ProviderLocationMapping>>;
}

// SearchProviderLocationMappings Service
export const { searchProviderLocationMappings, refetchProviderLocationMapping, useSearchProviderLocationMappings } = ((): {
  searchProviderLocationMappings: ServiceMethod;
  refetchProviderLocationMapping: ServiceMethod;
  useSearchProviderLocationMappings: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchProviderLocationMappingsDocument, {
    onError,
  }).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<ProviderLocationMapping>> => {
        const args: SearchProviderLocationMappingResponseProviderLocationMappingConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query: payload,
          },
        };
        const opts = { merge: options?.merge ? mergeKey : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty(mergeKey, res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchProviderLocationMappings: fetch,
    refetchProviderLocationMapping: refetch,
    useSearchProviderLocationMappings: useService,
  };
})();
